import rolePermission from '@/constants/rolePermission';
import { DEFAULT_LAYOUT } from '../base';
import { AppRouteRecordRaw } from '../types';

const Production: AppRouteRecordRaw = {
  path: '/production',
  name: 'Production',
  component: DEFAULT_LAYOUT,
  meta: {
    title: '生产跟进',
    requiresAuth: true,
    icon: 'icon-brush',
    order: 4,
  },
  children: [
    {
      path: 'request-order',
      name: 'RequestOrder',
      component: () => import('@/views/production/requestOrder/index.vue'),
      meta: {
        title: '生产请求单',
        requiresAuth: true,
        roles: ['requestOrderGet'],
      },
    },
    {
      path: 'request-order-detail',
      name: 'RequestOrderDetail',
      component: () => import('@/views/production/requestOrder/detail.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '生产请求单详情',
        requiresAuth: true,
        roles: ['requestOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'plan-order',
      name: 'PlanOrder',
      component: () => import('@/views/production/planOrder/index.vue'),
      meta: {
        title: '生产计划单',
        requiresAuth: true,
        roles: ['producePlanOrderGet'],
      },
    },
    {
      path: 'plan-order-add',
      name: 'AddPlanOrder',
      component: async () => {
        const cpn = await import('@/views/production/planOrder/addOrEdit.vue');
        return { ...cpn.default, name: 'AddPlanOrder' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
      }),
      meta: {
        title: '新增生产计划单',
        requiresAuth: true,
        roles: rolePermission.producePlanOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'plan-order-batch-add',
      name: 'BatchAddPlanOrder',
      component: async () => {
        const cpn = await import('@/views/production/planOrder/addOrEdit.vue');
        return { ...cpn.default, name: 'BatchAddPlanOrder' };
      },
      props: (route: any) => ({
        batchAdd: route.query.batchAdd,
      }),
      meta: {
        title: '批量新增生产计划单',
        requiresAuth: true,
        roles: rolePermission.producePlanOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'plan-draft-order-edit',
      name: 'EditPlanDraftOrder',
      component: async () => {
        const cpn = await import('@/views/production/planOrder/addOrEdit.vue');
        return { ...cpn.default, name: 'EditPlanDraftOrder' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑生产计划单',
        requiresAuth: true,
        roles: ['producePlanOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'plan-order-details',
      name: 'PlanOrderDetails',
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      component: () => import('@/views/production/planOrder/details.vue'),
      meta: {
        title: '生产计划单详情',
        requiresAuth: true,
        roles: ['producePlanOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'inquiry-order-list',
      name: 'InquiryOrderList',
      component: () => import('@/views/production/inquiryOrder/index.vue'),
      meta: {
        title: '询价单',
        requiresAuth: true,
        roles: ['inquiryOrderSubmit'],
        sysConfigKey: 'afterInquirySubmitProduceOrder', // 系统配置的configKey，没有默认展示
        defaultValue: 0, // sysConfigKey的默认值，新用户取不到就会取默认值，没有设置默认展示
      },
    },
    {
      path: 'inquiry-order-add',
      name: 'AddInquiryOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inquiryOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddInquiryOrder' };
      },
      props: (route: Recordable) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
      }),
      meta: {
        title: '新增询价单',
        requiresAuth: true,
        roles: ['inquiryOrderSubmit', 'supplierInfoFactoryGetDataPermission'],
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'inquiry-order-edit',
      name: 'EditInquiryOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inquiryOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditInquiryOrder' };
      },
      props: (route: Recordable) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑询价单',
        requiresAuth: false,
        hideInMenu: true,
        roles: ['supplierInfoFactoryGetDataPermission'],
      },
    },
    {
      path: 'inquiry-order-details',
      name: 'InquiryOrderDetails',
      component: () => import('@/views/production/inquiryOrder/details.vue'),
      props: (route: Recordable) => ({
        id: route.query.id,
      }),
      meta: {
        title: '询价单详情',
        requiresAuth: false,
        hideInMenu: true,
        roles: ['supplierInfoFactoryGetDataPermission'],
      },
    },
    {
      path: 'production-order',
      name: 'ProductionOrder',
      component: () => import('@/views/production/productionOrder/index.vue'),
      meta: {
        title: '生产单',
        requiresAuth: true,
        roles: rolePermission.produceOrderGet,
        permissionMode: 'some',
      },
    },
    {
      path: 'scheduling',
      name: 'ProductionScheduling',
      component: () => import('@/views/production/scheduling/index.vue'),
      meta: {
        title: '生产排产',
        requiresAuth: true,
        roles: ['produceOrderSchedulingLook'],
        permissionMode: 'some',
      },
    },
    {
      path: 'process-audit',
      name: 'ProcessAudit',
      component: () => import('@/views/production/processAudit/index.vue'),
      meta: {
        title: '工序审核',
        requiresAuth: true,
        sysConfigKey: 'isOpenWorkProcessEditAudit', // 系统配置的configKey，没有默认展示
        defaultValue: 0, // sysConfigKey的默认值，新用户取不到就会取默认值，没有设置默认展示
        roles: ['goodsDataWorkProcessGet', ...rolePermission.produceOrderGet],
        permissionMode: 'some',
      },
    },
    {
      path: 'production-order-bom',
      name: 'ProductionOrderBom',
      component: async () => {
        const cpn = await import('@/views/production/productionOrder/bom.vue');
        return { ...cpn.default, name: 'ProductionOrderBom' };
      },
      meta: {
        title: '生产制单',
        requiresAuth: true,
        roles: rolePermission.produceOrderGet,
        permissionMode: 'some',
        hideInMenu: true,
        iframe: true,
      },
    },
    {
      path: 'production-order-add',
      name: 'AddProductionOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/productionOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddProductionOrder' };
      },
      props: (route: any) => ({
        copy: route.query.copy,
        ordersSn: route.query.ordersSn,
        planToProduce: route.query.planToProduce,
        goodsId: route.query.goodsId,
      }),
      meta: {
        title: '新增生产单',
        requiresAuth: true,
        roles: rolePermission.produceOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'production-draft-order-edit',
      name: 'EditProductionDraftOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/productionOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditProductionDraftOrder' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑生产单',
        requiresAuth: true,
        roles: rolePermission.produceOrderDt,
        permissionMode: 'general',
        hideInMenu: true,
      },
    },
    {
      path: 'production-order-details',
      name: 'ProductionOrderDetails',
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      component: () => import('@/views/production/productionOrder/details.vue'),
      meta: {
        title: '生产单详情',
        requiresAuth: true,
        roles: rolePermission.produceOrderDt,
        permissionMode: 'general',
        hideInMenu: true,
      },
    },
    {
      path: 'inbound-order',
      name: 'InboundOrder',
      component: () => import('@/views/production/inboundOrder/index.vue'),
      meta: {
        title: '生产入库',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
      },
    },
    {
      path: 'inbound-order-add',
      name: 'AddInboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddInboundOrder' };
      },
      meta: {
        title: '新增生产入库单',
        requiresAuth: true,
        roles: rolePermission.produceStoreInOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'inbound-order-edit',
      name: 'EditInboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/inboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditInboundOrder' };
      },
      meta: {
        title: '编辑生产入库单',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'inbound-order-detail',
      name: 'inboundOrderDetail',
      component: () => import('@/views/production/inboundOrder/details.vue'),
      meta: {
        title: '生产入库单详情',
        requiresAuth: true,
        roles: ['produceStoreInOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'goods-rework',
      name: 'GoodsRework',
      component: () => import('@/views/production/goodsRework/index.vue'),
      meta: {
        title: '次品返厂',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
      },
    },
    {
      path: 'goods-rework-add',
      name: 'AddGoodsRework',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddGoodsRework' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
        businessSn: route.query.businessSn,
        orderType: route.query.orderType,
        businessId: route.query.businessId,
      }),
      meta: {
        title: '新增次品返厂单',
        requiresAuth: true,
        roles: rolePermission.reworkOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'goods-rework-edit',
      name: 'EditGoodsRework',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditGoodsRework' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑次品返厂单',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'goods-rework-edit',
      name: 'GoodsReworkDetail',
      component: async () => {
        const cpn = await import(
          '@/views/production/goodsRework/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'GoodsReworkDetail' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '次品返厂单详情',
        requiresAuth: true,
        roles: ['reworkOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'outbound-order',
      name: 'OutboundOrder',
      component: () => import('@/views/production/outboundOrder/index.vue'),
      meta: {
        title: '生产出库',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
      },
    },
    {
      path: 'outbound-order-add',
      name: 'AddOutboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/outboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddOutboundOrder' };
      },
      meta: {
        title: '新增生产出库单',
        requiresAuth: true,
        roles: rolePermission.produceStoreOutOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'outbound-order-edit',
      name: 'EditOutboundOrder',
      component: async () => {
        const cpn = await import(
          '@/views/production/outboundOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditOutboundOrder' };
      },
      meta: {
        title: '编辑生产出库单',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'outbound-order-detail',
      name: 'outboundOrderDetail',
      component: () => import('@/views/production/outboundOrder/detail.vue'),
      meta: {
        title: '生产出库单详情',
        requiresAuth: true,
        roles: ['produceStoreOutOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'outbound-return',
      name: 'OutboundReturn',
      component: () => import('@/views/production/outboundReturn/index.vue'),
      meta: {
        title: '出库退货',
        requiresAuth: true,
        roles: ['goodsRejectOrderGet'],
      },
    },
    {
      path: 'outbound-return-detail',
      name: 'OutboundReturnDetail',
      component: () => import('@/views/production/outboundReturn/detail.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '出库退货单详情',
        requiresAuth: true,
        roles: ['goodsRejectOrderGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'outbound-return-add',
      name: 'AddOutboundReturn',
      component: () =>
        import('@/views/production/outboundReturn/addOrEdit.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
        businessSn: route.query.businessSn,
      }),
      meta: {
        title: '新增出库退货单',
        requiresAuth: true,
        roles: rolePermission.goodsRejectOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'outbound-return-edit',
      name: 'EditOutboundReturn',
      component: () =>
        import('@/views/production/outboundReturn/addOrEdit.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑出库退货单',
        requiresAuth: true,
        roles: ['goodsRejectOrderGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'production-outsource',
      name: 'ProductionOutsource',
      component: () =>
        import('@/views/production/productionOutsource/index.vue'),
      meta: {
        title: '工序外发管理',
        requiresAuth: true,
        roles: ['outwardOrderGet'],
      },
    },
    {
      path: 'production-outsource-add',
      name: 'AddProductionOutsource',
      component: () =>
        import('@/views/production/productionOutsource/addOrEdit.vue'),
      meta: {
        title: '新增工序外发',
        requiresAuth: true,
        roles: rolePermission.outwardOrderEdit,
        hideInMenu: true,
      },
    },
    {
      path: 'work-ticket',
      name: 'WorkTicket',
      component: () => import('@/views/production/workTicket/index.vue'),
      meta: {
        title: '裁床编菲',
        requiresAuth: true,
        roles: rolePermission.produceOrderGet.map((p: string) => {
          return [p, 'cutBedTicketGet'];
        }),
        permissionMode: 'general',
        notOnline: false,
      },
    },
    {
      path: 'work-ticket-info',
      name: 'WorkTicketInfo',
      component: () =>
        import('@/views/production/workTicket/workTicketInfo.vue'),
      meta: {
        title: '裁床编菲明细',
        requiresAuth: true,
        roles: rolePermission.produceOrderGet.map((p: string) => {
          return [p, 'cutBedTicketGet'];
        }),
        permissionMode: 'general',
        notOnline: false,
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'production-outsource-details',
      name: 'ProductionOutsourceDetails',
      component: () =>
        import('@/views/production/productionOutsource/details/index.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '外发详情',
        requiresAuth: true,
        hideInMenu: true,
        roles: ['outwardOrderGet'],
      },
    },
    {
      path: 'finished-inspections',
      name: 'FinishedInspections',
      component: () => import('@/views/production/inspectionsOrder/index.vue'),
      meta: {
        title: '质检单',
        requiresAuth: true,
        roles: ['inspectionGet'],
        notOnline: false,
      },
    },
    {
      path: 'add-finished-inspections',
      name: 'AddFinishedInspections',
      component: () =>
        import('@/views/production/inspectionsOrder/addOrEdit.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        type: route.query.type,
        orderType: route.query.orderType || 0,
        businessSn: route.query.businessSn || '',
        businessId: route.query.businessId || '',
      }),
      meta: {
        title: '新增质检单',
        requiresAuth: true,
        roles: ['inspectionAdd', 'inspectionDraft'],
        hideInMenu: true,
        notOnline: false,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'edit-finished-inspections',
      name: 'EditFinishedInspections',
      component: () =>
        import('@/views/production/inspectionsOrder/addOrEdit.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑质检单',
        requiresAuth: true,
        hideInMenu: true,
        notOnline: false,
      },
    },
    {
      path: 'finished-inspections-detail',
      name: 'FinishedInspectionsDetail',
      component: () => import('@/views/production/inspectionsOrder/detail.vue'),
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '质检单详情',
        requiresAuth: true,
        roles: ['inspectionGet'],
        hideInMenu: true,
        notOnline: false,
      },
    },
    {
      path: 'goods-repair',
      name: 'GoodsRepair',
      component: () => import('@/views/production/repairOrder/index.vue'),
      meta: {
        title: '次品返修',
        requiresAuth: true,
        roles: ['repairGet'],
      },
    },
    {
      path: 'goods-repair-add',
      name: 'AddGoodsRepair',
      component: async () => {
        const cpn = await import(
          '@/views/production/repairOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'AddGoodsRepair' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
        copy: route.query.copy,
        businessSn: route.query.businessSn,
        businessItem: route.query.businessItem,
        businessId: route.query.businessId,
      }),
      meta: {
        title: '新增次品返修单',
        requiresAuth: true,
        roles: rolePermission.repairOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'goods-repair-edit',
      name: 'EditGoodsRepair',
      component: async () => {
        const cpn = await import(
          '@/views/production/repairOrder/addOrEdit.vue'
        );
        return { ...cpn.default, name: 'EditGoodsRepair' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '编辑次品返修单',
        requiresAuth: true,
        roles: ['repairGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'goods-repair-detail',
      name: 'GoodsRepairDetail',
      component: async () => {
        const cpn = await import('@/views/production/repairOrder/detail.vue');
        return { ...cpn.default, name: 'GoodsRepairDetail' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '次品返修单详情',
        requiresAuth: true,
        roles: ['repairGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'sku-goods-repair-detail',
      name: 'SkuGoodsRepairDetail',
      component: async () => {
        const cpn = await import(
          '@/views/production/repairOrder/skuDetail.vue'
        );
        return { ...cpn.default, name: 'SkuGoodsRepairDetail' };
      },
      props: (route: any) => ({
        dtId: route.query.dtId,
      }),
      meta: {
        title: '单品返修详情',
        requiresAuth: true,
        roles: ['repairGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'outsource-repair',
      name: 'OutsourceRepair',
      component: () => import('@/views/production/outsourceRepair/index.vue'),
      meta: {
        title: '工序外发返修',
        requiresAuth: true,
        roles: ['outwardRepairGet'],
      },
    },
    {
      path: 'outsource-repair-detail',
      name: 'OutsourceRepairDetail',
      component: async () => {
        const cpn = await import(
          '@/views/production/outsourceRepair/detail.vue'
        );
        return { ...cpn.default, name: 'OutsourceRepairDetail' };
      },
      props: (route: any) => ({
        ordersSn: route.query.ordersSn,
      }),
      meta: {
        title: '工序外发返修详情',
        requiresAuth: true,
        roles: ['outwardRepairGet'],
        ignoreCache: true,
        hideInMenu: true,
      },
    },
    {
      path: 'outsource-recover',
      name: 'OutsourceRecover',
      component: () => import('@/views/production/outsourceRecover/index.vue'),
      meta: {
        title: '外发回收记录',
        requiresAuth: true,
        roles: ['outwardOrderGet'],
      },
    },
    {
      path: 'outsource-recover-detail',
      name: 'OutsourceRecoverDetail',
      component: async () => {
        const cpn = await import(
          '@/views/production/outsourceRecover/detail.vue'
        );
        return { ...cpn.default, name: 'OutsourceRecoverDetail' };
      },
      props: (route: any) => ({
        id: route.query.id,
      }),
      meta: {
        title: '工序外发回收',
        requiresAuth: true,
        roles: ['outwardOrderGet'],
        ignoreCache: true,
        hideInMenu: true,
      },
    },
  ],
};

export default Production;
